// A budget line's panel: funding by year against the previous budget's plan, the records tied to it,
// what the budget year buys, schedule, performers, congressional adds, and the justification book.
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorText } from "../api/client";
import type { BudgetDetail, SourceDocument } from "../api/types";
import { useUI, type PanelTarget } from "../app/context";
import { keys } from "../app/queries";
import { Badge } from "../components/Badge";
import { CongressTimeline } from "../components/CongressTimeline";
import { FundingChart } from "../components/FundingChart";
import { TrackBudget } from "../components/TrackBudget";
import { thresholdText } from "./Alerts";
import { changeWords, dueText, fy, lineRef, money } from "../lib/format";

export function BudgetPanel({ target }: { target: Extract<PanelTarget, { kind: "budget" }> }) {
  const { closePanel, openPanel, openViewer, showToast } = useUI();
  const [fetching, setFetching] = useState(false);
  const [tracking, setTracking] = useState(false);
  const q = useQuery({ queryKey: keys.budgetItem(target.id), queryFn: () => api<BudgetDetail>(`/api/budget/items/${encodeURIComponent(target.id)}`) });
  const b = q.data;
  if (q.isPending) return <div className="p-head"><p className="empty">Loading…</p></div>;
  if (q.isError || !b)
    return (
      <div className="p-head">
        <button className="x" aria-label="Close" onClick={closePanel}>×</button>
        <p className="empty">{errorText(q.error)}</p>
      </div>
    );
  const e = b.edition;
  const c = changeWords(b);
  const facts = [
    [`FY${e} request`, money(b.request)],
    [`FY${e - 1}`, money(b.current)],
    ["Change", c.text.replace(/[▲▼] /, (m) => (m.startsWith("▲") ? "+" : "−"))],
    b.planned != null ? [`FY${e - 1} budget planned`, money(b.planned)] : null,
    ...(b.kind === "cj"
      ? [b.project ? ["Budget activity", b.number_title] : null, ["Allocated by", b.budget_activity_title]]
      : [
          [b.kind === "rdte" ? "Program element" : b.kind === "om" ? "Sub-activity group" : "Line item", `${b.number}${b.number_title !== b.title ? " · " + b.number_title : ""}`],
          b.project && b.kind !== "om" ? ["Project", b.project] : null,
          ["Budget activity", [b.budget_activity, b.budget_activity_title].filter(Boolean).join(" · ")],
        ]),
    ["Appropriation", b.appropriation_title],
    ["Book", b.book.title],
  ].filter((f): f is [string, string] => !!f && !!f[1]);
  const openBook = async () => {
    setFetching(true);
    try {
      const d = await api<SourceDocument>(`/api/budget/items/${encodeURIComponent(b.id)}/document`);
      openViewer({ ...d, terms: [b.kind === "cj" ? "" : b.number, b.kind === "cj" ? "" : b.project, ...b.title.split(/\s+/).filter((w) => w.length > 4)].filter(Boolean), quote: "" });
    } catch (err) {
      showToast(errorText(err));
    } finally {
      setFetching(false);
    }
  };
  const perf = b.performers.filter((x) => x.activity);
  const sched = b.schedule.filter((m) => m.start);
  const quarter = (m: { fy: number; quarter: number | null } | null) => (m ? `${fy(m.fy)}${m.quarter ? " Q" + m.quarter : ""}` : "");
  const yy = String(e).slice(2);
  return (
    <>
      <div className="p-head">
        <div className="p-top">
          <div className="p-org">
            <Badge agency={b.agency + " " + b.service} small />
            <span>
              {b.kind === "cj" ? lineRef(b) : `${b.service} › ${lineRef(b)}`}
            </span>
          </div>
          <button className="x" aria-label="Close" onClick={closePanel}>×</button>
        </div>
        {target.back ? (
          <button className="text-btn back" id="b-back" onClick={() => openPanel(target.back!)}>
            ← Back to {(target.from ?? "the record").slice(0, 60)}
          </button>
        ) : null}
        <h2>{b.title}</h2>
        <div className="p-actions">
          {b.book.pdf ? (
            <button className="btn v-open" id="b-doc" disabled={fetching} onClick={openBook}>
              {fetching ? "Fetching the book…" : b.kind === "cj" ? "View in the Congressional Justification" : "View in the justification book"}
            </button>
          ) : null}
          <button className="btn" id="b-track" aria-pressed={!!b.tracking} onClick={() => setTracking(true)}
            title={b.tracking ? thresholdText(b.tracking.threshold_pct, b.tracking.min_amount) : "Alerts when this line changes"}>
            {b.tracking ? "🔔 Tracking" : "🔔 Track"}
          </button>
          <span className="spacer" />
          {/^https?:/.test(b.book.url) ? (
            <a className="btn" href={b.book.url} target="_blank" rel="noopener noreferrer">
              Open original
            </a>
          ) : null}
        </div>
        <dl className="facts">
          {facts.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="p-body">
        <h4>Funding by year</h4>
        <FundingChart b={b} />
        <CongressTimeline marks={b.marks ?? []} />
        {b.records.length ? (
          <>
            <h4>Records tied to this line</h4>
            <div className="rows tight">
              {b.records.map((r) => (
                <div key={r.key} className="row mini" data-rec={r.key} role="button" tabIndex={0} onClick={() => openPanel({ kind: "notice", key: r.key, feedId: null })}>
                  <Badge agency={r.agency} small />
                  <div style={{ minWidth: 0 }}>
                    <div className="t">{r.title}</div>
                    <div className="m">
                      <span className={`strength ${r.strength}`}>{r.strength === "linked" ? "Linked" : "Possible"}</span>
                      {" "}
                      {r.horizon === "upcoming" ? (r.window?.text ?? "Forecast") : r.horizon === "context" ? "News" : dueText(r)}
                      {" "}
                      {r.buyer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
        {b.program.length ? (
          <>
            {/* An O&M sub-activity group lists its changes from the year before, not what each program buys. */}
            <h4>{b.kind === "om" ? `FY${yy} program changes` : `What FY${yy} buys`}</h4>
            {b.program.map((p, i) => (
              <div key={i} className="plan-item">
                <h5>
                  {p.title || (b.kind === "om" ? "Change" : "")}
                  {p.funding[String(e)] != null ? (
                    <span>
                      {b.kind === "om"
                        ? `${(p.funding[String(e)] ?? 0) > 0 ? "+" : ""}${money(p.funding[String(e)])}`
                        : `${money(p.funding[String(e)])} in FY${yy}`}
                    </span>
                  ) : null}
                </h5>
                {p.budget ? <div className="body-text">{p.budget}</div> : null}
                {p.current ? (
                  <details>
                    <summary>FY{String(e - 1).slice(2)} plan</summary>
                    <div className="body-text">{p.current}</div>
                  </details>
                ) : null}
              </div>
            ))}
          </>
        ) : null}
        {b.mission || b.element_mission ? (
          <>
            <h4>{b.kind === "rdte" ? "Mission" : b.kind === "cj" ? "Program description" : b.kind === "om" ? (b.project ? "Program changes" : "Operations financed") : "Description"}</h4>
            <div className="body-text">{b.mission || b.element_mission}</div>
          </>
        ) : null}
        {b.justification ? (
          <>
            <h4>{b.kind === "om" ? "Force structure" : "Justification"}</h4>
            <div className="body-text">{b.justification}</div>
          </>
        ) : null}
        {sched.length ? (
          <>
            <h4>Schedule</h4>
            <ul className="sched">
              {sched.slice(0, 40).map((m, i) => (
                <li key={i} className={m.upcoming ? "up" : ""}>
                  <span className="when">
                    {quarter(m.start)}
                    {m.end && quarter(m.end) !== quarter(m.start) ? "–" + quarter(m.end) : ""}
                  </span>
                  <span>
                    {m.event}
                    {m.group && m.group !== m.event ? <small> {m.group}</small> : null}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {perf.length ? (
          <>
            <h4>Who does the work</h4>
            <table className="perf">
              <thead>
                <tr>
                  <th>Performer</th>
                  <th>For</th>
                  <th>Contract</th>
                  <th>FY{yy}</th>
                </tr>
              </thead>
              <tbody>
                {perf.slice(0, 40).map((x, i) => (
                  <tr key={i}>
                    <td>
                      <b>{x.activity}</b>
                      <span>{x.location}</span>
                    </td>
                    <td>{x.name}</td>
                    <td>
                      {[x.method, x.type].filter(Boolean).join(" / ")}
                      {x.awards[String(e)] ? <span>award {x.awards[String(e)]}</span> : null}
                    </td>
                    <td>{money(x.funding[String(e)])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {perf.length > 40 ? <p className="muted-s">{perf.length - 40} more in the book.</p> : null}
          </>
        ) : null}
        {b.adds.length ? (
          <>
            <h4>Congressional adds</h4>
            <ul className="reasons">
              {b.adds.map((a, i) => (
                <li key={i}>
                  <b>{a.title}</b>
                  {a.funding != null ? ` · ${money(a.funding)}` : ""}
                  {a.text ? <div className="body-text">{a.text}</div> : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {b.acquisition ? (
          <>
            <h4>{b.kind === "cj" ? "How the money is allocated" : "Acquisition strategy"}</h4>
            <div className="body-text">{b.acquisition}</div>
          </>
        ) : null}
        {b.change_summary ? (
          <>
            <h4>Why the program element changed</h4>
            <div className="body-text">{b.change_summary}</div>
          </>
        ) : null}
      </div>
      <TrackBudget b={b} open={tracking} onClose={() => setTracking(false)} />
    </>
  );
}
